export default class FormsHandler {
    constructor() {
        this.messageHandlers = [];
        this.heightChecks = [];
        this.observers = [];
        this.hubspotForms = [];
    }

    init() {
        this.loadHubSpotAPI();
        this.initAPIForms();
        this.initContactForms();
        this.initGlobalFormListener();
    }

    // Load HubSpot Forms API if not already loaded
    loadHubSpotAPI() {
        if (window.hbspt) return;

        const script = document.createElement('script');
        script.src = '//js.hsforms.net/forms/v2.js';
        script.charset = 'utf-8';
        script.async = true;
        document.head.appendChild(script);
    }

    // Validate redirect URLs for security
    isValidRedirect(url) {
        try {
            // List of allowed domains
            const allowedDomains = [
                window.location.hostname // Allow current domain
            ];

            // Create URL object (handles relative and absolute URLs)
            const urlObj = new URL(url, window.location.origin);

            // Check if the URL's hostname matches any allowed domain
            const isAllowed = allowedDomains.some(domain =>
                urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain)
            );

            return isAllowed;
        } catch (e) {
            // Invalid URL format
            return false;
        }
    }

    // Get redirect URL from query string
    getRedirectUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        // Check multiple possible parameter names
        return urlParams.get('redirect') ||
               urlParams.get('redirect_url') ||
               urlParams.get('return_url') ||
               urlParams.get('Redirect_URL');
    }

    // Initialize HubSpot forms using API method
    initAPIForms() {
        const formContainers = document.querySelectorAll('.hubspot-form-container');
        if (formContainers.length === 0) return;

        formContainers.forEach((container, index) => {
            const region = container.dataset.region;
            const portalId = container.dataset.portalId;
            const formId = container.dataset.formId;
            const redirectUrl = container.dataset.redirectUrl;

            // Validate required fields
            if (!region || !portalId || !formId) {
                console.error('Missing required HubSpot form parameters');
                return;
            }

            // Add unique ID to container if it doesn't have one
            if (!container.id) {
                container.id = `hubspot-form-${index}`;
            }

            // Wait for HubSpot API to load
            const initForm = () => {
                if (window.hbspt && window.hbspt.forms) {
                    const formConfig = {
                        region: region,
                        portalId: portalId,
                        formId: formId,
                        target: `#${container.id}`,
                        cssClass: 'hubspot-form'
                    };

                    // Add redirectUrl if provided and valid
                    if (redirectUrl && this.isValidRedirect(redirectUrl)) {
                        formConfig.redirectUrl = redirectUrl;
                    }

                    const formInstance = window.hbspt.forms.create(formConfig);

                    // Store form instance and container for cleanup
                    this.hubspotForms.push({ instance: formInstance, container: container });
                } else {
                    // Retry if API not loaded yet
                    setTimeout(initForm, 100);
                }
            };

            initForm();
        });
    }

    initContactForms() {
        const iframes = document.querySelectorAll('.form-iframe');
        if (iframes.length === 0) return;

        iframes.forEach(iframe => {
            this.setupIframeHandling(iframe);
        });
    }

    setupIframeHandling(iframe) {
        // Create a unified message handler for this iframe
        const messageHandler = (event) => {
            // Filter out React DevTools spam
            if (event.data && event.data.source === 'react-devtools-content-script') {
                return;
            }

            // Handle iframe height updates
            if (event.origin === 'https://forms-na2.hsforms.com' && event.data.height) {
                iframe.style.height = event.data.height + 'px';
            }

            // Handle HubSpot form submission with redirect
            if (event.data && event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormSubmit') {
                const redirectUrl = this.getRedirectUrl();

                if (redirectUrl) {
                    // Decode URL in case it's encoded
                    const decodedUrl = decodeURIComponent(redirectUrl);

                    // Validate the redirect URL for security
                    if (this.isValidRedirect(decodedUrl)) {
                        // Redirect after a short delay to ensure submission completes
                        setTimeout(() => {
                            window.location.href = decodedUrl;
                        }, 500);
                    } else {
                        console.warn('Invalid redirect URL blocked:', decodedUrl);
                    }
                }
            }
        };

        window.addEventListener('message', messageHandler);
        this.messageHandlers.push(messageHandler);

        // Handle iframe auto-resize
        iframe.addEventListener('load', () => {
            try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                if (iframeDoc) {
                    const resizeIframe = () => {
                        const height = iframeDoc.body.scrollHeight;
                        iframe.style.height = height + 'px';
                    };
                    
                    resizeIframe();
                    
                    // Watch for changes in iframe content
                    this.observer = new MutationObserver(resizeIframe);
                    this.observer.observe(iframeDoc.body, {
                        childList: true,
                        subtree: true,
                        attributes: true
                    });
                }
            } catch (e) {
                // Cross-domain iframe - auto-resize not available
                // Fallback: set a reasonable height
                iframe.style.height = '1300px';
            }
        });

        // Periodic height check (fallback)
        let attempts = 0;
        const heightCheck = setInterval(() => {
            attempts++;
            try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                if (iframeDoc && iframeDoc.body) {
                    const height = iframeDoc.body.scrollHeight;
                    if (height > 100) { // Valid content loaded
                        iframe.style.height = height + 'px';
                        clearInterval(heightCheck);
                    }
                }
            } catch (e) {
                // Cross-domain, stop trying after 10 attempts
                if (attempts > 10) {
                    clearInterval(heightCheck);
                }
            }
        }, 500);

        // Store interval and observer for cleanup
        this.heightChecks.push(heightCheck);
        if (this.observer) {
            this.observers.push(this.observer);
        }
    }

    // Global listener for all HubSpot form events
    initGlobalFormListener() {
        const globalHandler = (event) => {
            // Listen for HubSpot form submission events
            if (event.data && event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormSubmitted') {
                // Find the form container with redirect URL
                const formContainers = document.querySelectorAll('.hubspot-form-container');
                formContainers.forEach(container => {
                    const redirectUrl = container.dataset.redirectUrl;
                    if (redirectUrl && this.isValidRedirect(redirectUrl)) {
                        setTimeout(() => {
                            window.location.href = redirectUrl;
                        }, 500);
                    }
                });
            }
        };

        window.addEventListener('message', globalHandler);
        this.messageHandlers.push(globalHandler);
    }

    destroy() {
        // Clean up HubSpot API forms
        this.hubspotForms.forEach(({ container }) => {
            // Clear the container content
            if (container) {
                container.innerHTML = '';
            }
        });
        this.hubspotForms = [];

        // Clean up all message handlers
        this.messageHandlers.forEach(handler => {
            window.removeEventListener('message', handler);
        });
        this.messageHandlers = [];

        // Clean up all intervals
        this.heightChecks.forEach(interval => clearInterval(interval));
        this.heightChecks = [];

        // Clean up all observers
        this.observers.forEach(observer => observer.disconnect());
        this.observers = [];
    }
}